import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronLeft, MoreHorizontal } from "lucide-react";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { agentChannelsApi } from "@/api/agent-channels";
import { chatApi } from "@/api/chat";
import { MobileAvatar } from "@/components/mobile/mobile-avatar";
import { AgentStatusPill } from "@/components/mobile/agent-status-pill";
import { useMobileChannelSheet } from "@/components/layout/mobile-channel-context";

interface MobileChatHeaderProps {
  selectedAgentId: string | null;
  onOpenAgentSheet: () => void;
}

export function MobileChatHeader({ selectedAgentId, onOpenAgentSheet }: MobileChatHeaderProps) {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const channelSheet = useMobileChannelSheet();

  const agent = agents.find((a) => a.id === selectedAgentId);
  const activeChannelId = searchParams.get("channelId") || "general";

  const { data: channels = [] } = useQuery({
    queryKey: ["agent-channels", selectedAgentId],
    queryFn: () => agentChannelsApi.list(selectedAgentId!),
    enabled: !!selectedAgentId,
  });

  const { data: sessions = [] } = useQuery({
    queryKey: ["chat-sessions", selectedAgentId],
    queryFn: () => chatApi.listSessions(selectedAgentId!),
    enabled: !!selectedAgentId,
  });

  const activeChannel = useMemo(() => {
    if (activeChannelId === "general") {
      return { id: "general", name: "General", color: "#9CA3AF" };
    }
    const ch = channels.find((c) => c.id === activeChannelId);
    return ch
      ? { id: ch.id, name: ch.name, color: ch.color || "#9CA3AF" }
      : { id: "general", name: "General", color: "#9CA3AF" };
  }, [channels, activeChannelId]);

  const sessionCount = useMemo(() => {
    if (activeChannelId === "general") return sessions.filter((s) => !s.channelId).length;
    return sessions.filter((s) => s.channelId === activeChannelId).length;
  }, [sessions, activeChannelId]);

  return (
    <header
      className="glass-chrome mg-header sm:hidden sticky top-0 z-30 shrink-0 pb-2"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      {/* Identity row */}
      <div className="flex h-14 items-center gap-2 px-2.5">
        <button
          type="button"
          onClick={() => navigate(`/tenants/${tenantId}/dashboard`)}
          className="mg-icon-btn"
          aria-label="Back"
        >
          <ChevronLeft size={20} />
        </button>

        <button
          type="button"
          onClick={onOpenAgentSheet}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-full px-1.5 py-1 text-left hover:bg-white/30 dark:hover:bg-white/10"
        >
          {agent ? (
            <>
              <MobileAvatar
                name={agent.name}
                avatarUrl={agent.avatarUrl}
                size={30}
                status={agent.status}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 text-[14px] font-semibold leading-tight text-text-primary">
                  <span className="truncate">{agent.name}</span>
                  <ChevronDown size={12} className="shrink-0 text-text-muted" />
                </div>
                <AgentStatusPill status={agent.status} mini />
              </div>
            </>
          ) : (
            <>
              <div className="flex h-[30px] w-[30px] items-center justify-center rounded-md bg-accent/10 text-[11px] font-semibold text-accent">
                ?
              </div>
              <span className="truncate text-[14px] text-text-muted">Select agent</span>
              <ChevronDown size={12} className="shrink-0 text-text-muted" />
            </>
          )}
        </button>

        <button type="button" className="mg-icon-btn" aria-label="More">
          <MoreHorizontal size={18} />
        </button>
      </div>

      {/* Channel context chip row */}
      <div className="flex items-center gap-1.5 px-3 pt-1">
        <button
          type="button"
          onClick={() => channelSheet?.openChannelSheet()}
          disabled={!selectedAgentId || !channelSheet}
          className="glass-field inline-flex h-[26px] items-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold text-text-secondary disabled:opacity-50"
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: activeChannel.color }}
          />
          {activeChannel.name}
          <span className="text-text-muted">· {sessionCount} sessions</span>
          <ChevronDown size={11} className="text-text-muted" />
        </button>
        <span className="ml-auto text-[11px] text-text-muted">Today</span>
      </div>
    </header>
  );
}
